/**
 * Everything about one account, in one dialog.
 *
 * There were three buttons on this screen - 編集 for the name, 変更 beside
 * プラットフォーム権限, and 所属を変更 - each opening its own dialog, each saving
 * one part. Nobody thinks of it as three things: an operator opens a person to
 * fix that person. So this is one dialog with sections, and Save applies each
 * part that actually changed.
 *
 * Applied in order, and the order is not arbitrary. Identity first, because it
 * is the part that cannot fail for a reason the operator has to resolve
 * elsewhere. Then the platform role, because promoting somebody to JMC operator
 * has to strip their affiliation before an affiliation can be written. Then the
 * affiliation itself.
 *
 * What sharing a dialog does NOT do is relax any of the controls the separate
 * ones carried:
 *
 *   The platform role still re-authenticates. `POST .../platform-role` is behind
 *   step-up on the server; the dialog says so before it is saved rather than
 *   surprising somebody with a re-auth prompt mid-save.
 *
 *   A password is still its own act. Setting one takes an account over, so it
 *   is a section that does nothing unless deliberately filled in - never a
 *   field carried along by a save that was about a display name.
 *
 *   The rules that make states impossible are still the server's. A JMC
 *   operator holds no membership (migration 011); a person belongs to one
 *   clinic (migration 074). The dialog hides what it can and the database
 *   refuses the rest.
 */
import { roleOptions, type TenantRoleName } from '../roles.ts';
import { useEffect, useState } from 'react';
import type { AdminUserDetail } from '../api.ts';
import { patch, post } from '../api.ts';
import { Field, FieldGroup } from './EditPanel.tsx';
import { ModalForm } from './Modal.tsx';
import { Select } from './Select.tsx';
import { StoreChecklist } from './StoreChecklist.tsx';
import { PASSWORD_HINT, MIN_PASSWORD_LENGTH } from '../../../shared/password-policy.ts';

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '有効' },
  { value: 'SUSPENDED', label: '停止' },
  { value: 'ARCHIVED', label: 'アーカイブ' },
];

export interface Affiliation {
  clinicId: string | null;
  clinicName: string | null;
  clinicAdmin: boolean;
  /** The one role held in that clinic. */
  role: TenantRoleName;
  stores: Array<{ id: string; name: string }>;
}

export function UserEditor({
  user,
  affiliation,
  clinics,
  stores,
  open,
  onClose,
}: {
  user: AdminUserDetail;
  affiliation: Affiliation;
  clinics: Array<{ id: string; name: string }>;
  stores: Array<{ id: string; name: string; clinic_id: string }>;
  open: boolean;
  onClose: () => void;
}) {
  const [displayName, setDisplayName] = useState(user.display_name);
  const [email, setEmail] = useState(user.email ?? '');
  const [status, setStatus] = useState(user.status);
  const [platformRole, setPlatformRole] = useState(user.platform_role ?? '');

  const [clinicId, setClinicId] = useState(affiliation.clinicId ?? '');
  const [role, setRole] = useState<TenantRoleName>(affiliation.role);
  const clinicAdmin = role === 'CLINIC_ADMIN';
  const [chosen, setChosen] = useState<string[]>(affiliation.stores.map((store) => store.id));

  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);

  // Reopening starts from what is stored, never from an abandoned edit.
  useEffect(() => {
    if (!open) return;
    setDisplayName(user.display_name);
    setEmail(user.email ?? '');
    setStatus(user.status);
    setPlatformRole(user.platform_role ?? '');
    setClinicId(affiliation.clinicId ?? '');
    setRole(affiliation.role);
    setChosen(affiliation.stores.map((store) => store.id));
    setPassword('');
    setClearPassword(false);
  }, [open, user, affiliation]);

  /*
   * 'provisioned' is the issuer of an account nobody has claimed yet, which is
   * the only state in which the address is still ours to correct rather than
   * the provider's to assert on every sign-in.
   */
  const claimed = user.issuer !== 'provisioned';
  const becomingOperator = platformRole === 'JMC_ADMIN';
  const inClinic = stores.filter((store) => store.clinic_id === clinicId);
  const affiliationChanged =
    clinicId !== (affiliation.clinicId ?? '') ||
    role !== affiliation.role ||
    chosen.join() !== affiliation.stores.map((store) => store.id).join();

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title="ユーザーを編集"
      caption={user.email ?? undefined}
      submitLabel="保存する"
      wide
      onSubmit={async () => {
        /*
         * No pre-flight refusal for "operator with an affiliation".
         *
         * This used to stop here and tell the operator to go and revoke the
         * membership on another screen first. They had already said what they
         * wanted by choosing JMC管理者; the server now ends the affiliation as
         * part of granting the role (see POST /users/:id/platform-role), which
         * is the same decision expressed once instead of four times.
         */
        if (!becomingOperator && clinicId && !clinicAdmin && chosen.length === 0) {
          throw new Error('担当店舗を1つ以上選ぶか、クリニック管理に変更してください。');
        }

        // 1. Identity.
        await patch(`/admin/users/${user.id}`, {
          displayName,
          status,
          ...(claimed ? {} : { email }),
        });

        // 2. The platform role, which the server puts behind re-authentication.
        if ((user.platform_role ?? '') !== platformRole) {
          await post(`/admin/users/${user.id}/platform-role`, {
            platformRole: becomingOperator ? 'JMC_ADMIN' : null,
          });
        }

        // 3. The affiliation, which a JMC operator does not have.
        if (!becomingOperator && clinicId && affiliationChanged) {
          const body = {
            role,
            ...(clinicAdmin ? {} : { storeIds: chosen }),
          };
          if (affiliation.clinicId) {
            await patch(`/admin/clinics/${clinicId}/members/${user.id}`, body);
          } else {
            if (!email) throw new Error('メールアドレスのないアカウントに所属は付けられません。');
            await post(`/admin/clinics/${clinicId}/members`, { email, ...body });
          }
        }

        // 4. The password, only when this dialog was actually used to set one.
        if (clearPassword || password) {
          await post(`/admin/users/${user.id}/password`, {
            newPassword: clearPassword ? null : password,
          });
        }
      }}
    >
      <FieldGroup title="基本情報" />
      <Field label="表示名" htmlFor="ue-name">
        <input
          id="ue-name"
          maxLength={200}
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </Field>
      <Field
        label="メールアドレス"
        htmlFor="ue-email"
        hint={
          claimed
            ? 'ログイン済みのため変更できません。ご本人のGoogleアカウントのアドレスです。'
            : 'このアドレスでログインした人がこのアカウントを引き継ぎます。'
        }
      >
        <input
          id="ue-email"
          type="email"
          maxLength={320}
          value={email}
          disabled={claimed}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>
      <Field label="アカウント状態" htmlFor="ue-status" hint="停止するとログインできなくなります。">
        <Select id="ue-status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
      </Field>

      <FieldGroup title="区分と所属" note="JMC管理者はクリニックに所属しません。" />
      <Field label="区分" htmlFor="ue-platform" hint="変更時は再認証を求められます。">
        <Select
          id="ue-platform"
          value={platformRole}
          onChange={setPlatformRole}
          options={[
            { value: '', label: 'クリニック側ユーザー' },
            { value: 'JMC_ADMIN', label: 'JMC管理者' },
          ]}
        />
      </Field>

      {!becomingOperator && (
        <>
          <Field
            label="所属クリニック"
            htmlFor="ue-clinic"
            hint={
              affiliation.clinicId
                ? '別のクリニックへ移す場合は、先に所属を解除してください。'
                : '所属できるクリニックは1つです。'
            }
          >
            <Select
              id="ue-clinic"
              value={clinicId}
              onChange={(next) => {
                setClinicId(next);
                setChosen([]);
              }}
              disabled={affiliation.clinicId !== null}
              placeholder="所属なし"
              options={clinics.map((clinic) => ({ value: clinic.id, label: clinic.name }))}
            />
          </Field>

          {clinicId && (
            <Field label="権限" htmlFor="ue-role">
              <Select
                id="ue-role"
                value={role}
                onChange={(next) => setRole(next as TenantRoleName)}
                options={roleOptions(true)}
              />
            </Field>
          )}

          {clinicId && !clinicAdmin && (
            <StoreChecklist
              idPrefix="ue-stores"
              stores={inClinic}
              value={chosen}
              onChange={setChosen}
            />
          )}
        </>
      )}

      {becomingOperator && affiliation.clinicId && (
        <p className="alert field--full">
          このアカウントは{affiliation.clinicName}に所属しています。
          JMC管理者に変更するには、先に所属を解除してください。
        </p>
      )}

      {/*
        The password is a section that does nothing unless it is filled in.

        Never a field that rides along with a save about a display name: setting
        one takes the account over, ends every session that person has, and is
        the one thing on this screen somebody could do by accident and not
        notice.
      */}
      <FieldGroup
        title="パスワード"
        note={
          user.has_password
            ? 'このアカウントはパスワードでログインできます。'
            : 'このアカウントにパスワードは設定されていません（Googleログインのみ）。'
        }
      />
      <Field
        label="新しいパスワード"
        htmlFor="ue-password"
        hint={`空欄なら変更しません。${PASSWORD_HINT}。設定すると、このユーザーのログイン中のセッションはすべて終了します。`}
      >
        <input
          id="ue-password"
          type="password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          maxLength={200}
          value={password}
          disabled={clearPassword}
          placeholder={PASSWORD_HINT}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
      {user.has_password && (
        <Field label="パスワードを削除" htmlFor="ue-clear" hint="Googleログインのみに戻します。">
          <label className="checkline" htmlFor="ue-clear">
            <input
              id="ue-clear"
              type="checkbox"
              checked={clearPassword}
              onChange={(event) => {
                setClearPassword(event.target.checked);
                if (event.target.checked) setPassword('');
              }}
            />
            <span>パスワードでのログインを無効にする</span>
          </label>
        </Field>
      )}
    </ModalForm>
  );
}
